import axios from 'axios';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from 'app/components/ui/alert';
import { CartStepsProps } from 'app/pages/RegistrationFormsPage';
import { Info } from 'lucide-react';
import TagManager from 'react-gtm-module';
import { useDispatch } from 'react-redux';
import { setVendorData } from 'store/vendorSlice';
import useEmbedResize from 'utils/custom-hooks/useEmbedResize';
import { parseOrderItemsForGTM } from 'utils/gtmUtils';
import { EventFieldType } from '../../../types/EventField';
import DownloadOrderInvoiceButton from '../DownloadOrderInvoiceButton/DownloadOrderInvoiceButton';
import GetYourTicketsButton from '../GetYourTicketsButton/GetYourTicketsButton';
import RegistrationFormAccordion from '../RegistrationFormAccordion/RegistrationFormAccordion';
import RegistrationFormsPageSkeleton from '../Skeletons/RegistrationFormsPageSkeleton';
import { parseOrderItemsIntoRegistrationForms } from './parseOrderItemsIntoRegistrationForms';

export interface DisplayedTicketRegistrationForms {
  name: string;
  code: string;
  seat: string;
  download: {
    pdf: string;
    pkpass: string;
    png: string;
  };
  id_order_item: string;
  index: number;
  series: string;
  ticket_type_id: number;
  registration_form: any;
  addons: DisplayedTicketAddon[] | null;
  wallet_credit: DisplayedTopUp | null;
  dateTime?: {
    date: string;
    timeStart: string;
    timeEnd: string;
  };
}

export interface DisplayedTicketAddon {
  id: string;
  name: string;
  uses: number;
}

export interface DisplayedTopUp {
  amount: number;
  currency: string;
}

interface PageData {
  isLoading: boolean;
  status: string;
  fields: EventFieldType[];
  registrationForms: any;
  eventId: string;
  invoice: string;
  hasAddonsOrTopUps: boolean;
  registrationFormsCanBeEditedAfterSubmission: boolean;
  displayTickets: boolean;
}

const initialPageData = {
  isLoading: true,
  fields: [],
  registrationForms: [],
  numberOfTickets: 0,
  eventId: '',
  status: '',
  invoice: '',
  hasAddonsOrTopUps: false,
  registrationFormsCanBeEditedAfterSubmission: false,
  displayTickets: true,
};

interface ParticipantsInformationProps {
  handleCartStepsProps?: (data: CartStepsProps) => void;
}

const ParticipantsInformation: React.FC<ParticipantsInformationProps> = ({
  handleCartStepsProps,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('uuid');
  const [pageData, setPageData] = useState<PageData>(initialPageData);
  const [ticketsButtonIsDisabled, setTicketsButtonIsDisabled] = useState(false);

  useEffect(() => {
    const getData = async () => {
      const { data: response } = await axios.post(
        `${process.env.REACT_APP_API_URL}/order?include=hasAttendeeForms,invoices,mustCompleteRegistrationForms,event.registrationform,event.seller_public_information,event.sellerFeatures,event.registrationFormsCanBeEditedAfterSubmission,event.checkoutConfiguration.displayTickets,order_items,order_items.registration_form,order_items.id,order_items.ticketTypeId,order_items.name,order_items.tickets,order_items.details,order_total`,
        { order_id: orderId },
      );

      const registrationFields = response.event.registrationform;

      const sortedFields = [...registrationFields].sort(
        (a, b) => a.order - b.order,
      );

      if (handleCartStepsProps) {
        handleCartStepsProps({
          displayAddonsStep: response.hasAddonsOrTopUps,
          isLoading: false,
        });
      }

      setTicketsButtonIsDisabled(response.mustCompleteRegistrationForms);

      const registrationForms = parseOrderItemsIntoRegistrationForms(
        response.order_items,
      );

      setPageData({
        isLoading: false,
        status: response.status,
        fields: sortedFields,
        registrationFormsCanBeEditedAfterSubmission:
          response.event.registrationFormsCanBeEditedAfterSubmission,
        registrationForms,
        eventId: response.event.id,
        invoice: response.invoices[0] || '',
        hasAddonsOrTopUps: response.hasAddonsOrTopUps,
        displayTickets: response.event.checkoutConfiguration.displayTickets,
      });
      dispatch(
        setVendorData({
          id: response.event.id_seller,
          features: response.event.sellerFeatures,
          publicInformation: response.event.seller_public_information,
        }),
      );

      // GTM
      const tagManagerArgs = {
        gtmId: response.event.gtm_code,
        dataLayer: {
          event: 'purchase',
          ecommerce: {
            value: response.order_total.net_price,
            tax: response.order_total.tax_amount,
            transaction_id: response.uuid,
            currency: response.order_total.currency,
            items: parseOrderItemsForGTM(response.order_items),
          },
        },
      };

      TagManager.initialize(tagManagerArgs);
    };

    getData();
  }, []);

  const handleTicketsButtonEnableStatus = newValue => {
    setTicketsButtonIsDisabled(newValue);
  };

  // useEmbedResize([pageData.eventId]);

  const indexOfTheFirstAccordionToBeOpened =
    pageData.registrationForms.length > 1
      ? pageData.registrationForms.findIndex(el => el.data === null)
      : -1;

  return (
    <div>
      {pageData.isLoading ? (
        <RegistrationFormsPageSkeleton />
      ) : (
        <>
          <div className="mb-4 sm:mb-6 flex justify-between items-center flex-wrap gap-[8px]">
            <h2 className="title-md">
              {t(
                `${
                  pageData.registrationForms.length === 1
                    ? 'registrationFormPage'
                    : 'registrationFormsPage'
                }.sectionTitle`,
              )}
            </h2>
            {pageData.invoice ? (
              <DownloadOrderInvoiceButton downloadLink={pageData.invoice} />
            ) : null}
          </div>
          {pageData.registrationForms.length > 1 ? (
            <Alert>
              <Info size={16} />
              <AlertTitle>
                {t('registrationFormsPage.headerAlertTitle')}
              </AlertTitle>
              <AlertDescription>
                {t('registrationFormsPage.headerAlert')}
              </AlertDescription>
            </Alert>
          ) : null}
          <div id="registration-form" className="mt-4 sm:mt-6 mb-4">
            {pageData.registrationForms.map((registrationForm, index) => (
              <RegistrationFormAccordion
                key={`${registrationForm.ticket_type_id}-${registrationForm.index}`}
                isDisplayedIndividually={
                  pageData.registrationForms.length === 1
                }
                displayedIndex={index}
                registrationForm={registrationForm}
                fields={pageData.fields}
                orderId={orderId || ''}
                eventId={pageData.eventId}
                handleTicketsButtonEnableStatus={
                  handleTicketsButtonEnableStatus
                }
                // by default expand small registration forms
                expanded={
                  pageData.fields.length < 4
                    ? true
                    : indexOfTheFirstAccordionToBeOpened === index
                }
                registrationFormsCanBeEditedAfterSubmission={
                  pageData.registrationFormsCanBeEditedAfterSubmission
                }
              />
            ))}
          </div>
          <GetYourTicketsButton
            orderId={orderId || ''}
            disabled={ticketsButtonIsDisabled}
            orderHasMultipleTickets={pageData.registrationForms.length > 1}
            hasAddonsOrTopUps={pageData.hasAddonsOrTopUps}
            displayTickets={pageData.displayTickets}
          />
        </>
      )}
    </div>
  );
};

export default memo(ParticipantsInformation);
